import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../../components/loadingContainer";

import { Dropdown } from "primereact/dropdown";
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { Organization } from "../../api/models/organization.models";
import ContainerHeader from "../../components/containerHeader";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import EditOrganizationDialog from "../../dialogs/editOrganizationDialog";
import useCountries from "../../hooks/useCountries";
import { Button } from "primereact/button";
import useAdminReportingCycle from "../../hooks/useAdminReportingCycles";
import { useCycleItems } from "../../utils/cycleUtils";
import { SelectButton } from "primereact/selectbutton";
import { ReportingCycleEnum } from "../../api/models/reportingCycle.models";
import useAdminCompanyInvoicingTypes from "../../hooks/useAdminCompanyInvoicingTypes";
import { useInvoicingTypeItems } from "../../utils/companyInvoicingTypeUtils";
import { InvoicingTypeEnum } from "../../api/models/companyInvoicingType.models";
import { Company } from "../../api/models/companies.models";
import { CompanyInvoicingInformation } from "../../components/companyInvoicingInformation";
import { useNavigate } from "react-router";
import { Tooltip } from "primereact/tooltip";
import { useAdminDataContext } from "../../contexts/adminDataContext";
import usePipedriveIntegrations from "../../hooks/usePipedriveIntegrations";

import { CompanyContractInformation } from "../../components/companyContractInformation";
import { SynkkaIdentifiers } from "../../components/synkkaIdentifiers";

const OrganizationContainer = styled.div``;

const StyledTable = styled(DataTable)`
  padding-top: 2rem;

  & .edit-organization-link {
    color: var(--sumi-blue);
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: var(--blue-900);
    }
  }

  .p-datatable-tbody > tr > td {
    border: none;
    background-color: #fdfdfd;
    border-bottom: 1px solid #e1e1e1;
    padding: 1.5rem 1rem;
  }

  .p-datatable-thead > tr > th {
    border: none;
    font-weight: 500;
    font-size: 0.8rem;
    color: #888888;
    text-transform: uppercase;
  }

  // rotate svg icon
  & .p-row-toggler svg {
    color: var(--text-color);
    transform: rotate(90deg);
  }

  // rotate 180 degrees when attribute expanded is true
  & .p-row-toggler[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }

  .p-datatable-row-expansion {
    > td {
      padding: 0;
      border: none;
    }

    & .p-datatable-tbody > tr > td {
      background-color: var(--sumi-background-blue);
    }

    & .p-datatable-tbody > tr > td {
      border: none;
    }
  }
`;

const PaddedContainer = styled.div`
  padding: 6rem 3rem;
  width: 100%;
  text-align: left;
`;

const StyledAutoComplete = styled(AutoComplete)`
  width: 350px;
  margin: 0.5rem 0;
`;

const CompanySelect = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 40px;

  & label {
    font-size: 0.8rem;
    color: var(--text-color-secondary);
  }
`;

const ReportingTypeSelect = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 40px;
  position: relative;

  & label {
    font-size: 0.8rem;
    color: var(--text-color-secondary);
  }
`;

const YearSelectContainer = styled.div`
  display: flex;
  flex-direction: column;

  label {
    margin-bottom: 0.5rem;
  }
`;

const YearSelectDropdown = styled(Dropdown)`
  width: 200px;
  margin-bottom: 2px;
`;

const InvoicingTypeDropdown = styled(Dropdown)`
  width: 320px;
  margin-bottom: 2px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const StyledButton = styled(Button)`
  margin: 0 0 2rem 0;
  color: var(--sumi-blue);

  & .p-button-icon {
    margin-right: 0.5rem;
  }

  &:hover {
    color: var(--blue-900);
    text-decoration: underline;
  }
`;

const UserIcon = styled.i`
  color: var(--sumi-blue);
  cursor: pointer;
  border-radius: 4px;
  padding: 0.2rem;
  margin-right: 10px;

  &:focus {
    outline: 2px solid rgba(128, 128, 128, 0.5);
    outline-offset: 2px;
    box-shadow: 0 0 4px rgba(128, 128, 128, 0.3);
  }
`;

const LinksContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: nowrap;
`;

const AdminOrganizations: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showEditOrganization, setShowEditOrganization] =
    useState<Organization | null>(null);
  const [showNewOrganization, setShowNewOrganization] =
    useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [searchedCompany, setSearchedCompany] = useState<Company | null>(null);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);

  const businessIdentifierId = 50;
  const pipedriveIdentifierId = 200;
  const procountorIdentifierId = 250;

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [reportingType, setReportingType] = useState<
    ReportingCycleEnum | undefined
  >(undefined);

  const [invoicingYear, setInvoicingYear] = useState<number | null>(null);
  const [invoicingType, setInvoicingType] = useState<
    InvoicingTypeEnum | undefined
  >(undefined);

  const { companies, isLoadingCompanies } = useAdminDataContext();
  const { countries, isLoading: isLoadingCountries } = useCountries();

  const { updatePipeDriveInfo, isUpdatingPipeDriveInfo } =
    usePipedriveIntegrations();

  const isLoading = isLoadingCompanies || isLoadingCountries;

  const selectedCompanyId = selectedCompany?.id || null;

  const company = useMemo(() => {
    return companies?.find((c) => c.id === selectedCompanyId || 0) || null;
  }, [companies, selectedCompanyId]);

  // Filter organizations by selected company
  const filteredOrganizations = company?.organizations || [];

  const {
    reportingCycleData,
    isLoadingReportingCycle,
    updateReportingCycle,
    isUpdatingReportingCycle,
  } = useAdminReportingCycle({
    companyId: selectedCompanyId || 0,
    enabled: !!selectedCompanyId,
  });

  useEffect(() => {
    const selectedCycle = reportingCycleData?.find(
      (cycle) => cycle.year === selectedYear,
    );
    setReportingType(selectedCycle?.cycle);
  }, [selectedYear]);

  const {
    invoicingTypes,
    isLoadingInvoicingTypes,
    createInvoicingType,
    updateInvoicingType,
    isSavingInvoicingType,
  } = useAdminCompanyInvoicingTypes({
    companyId: selectedCompanyId || 0,
    enabled: !!selectedCompanyId,
  });

  useEffect(() => {
    const selectedInvoicingType = invoicingTypes?.find(
      (invoicingType) => invoicingType.year === invoicingYear,
    );
    setInvoicingType(selectedInvoicingType?.invoicingType);
  }, [invoicingYear, invoicingTypes]);

  const contractStartDate = useMemo(() => {
    if (company && company.contractStartDate) {
      return new Date(company.contractStartDate);
    }
    return null;
  }, [company]);

  const contractEndDate = useMemo(() => {
    if (company && company.contractEndDate) {
      return new Date(company.contractEndDate);
    }
    return null;
  }, [company]);

  const yearOptions = useMemo(() => {
    if (!contractStartDate) {
      return [];
    }

    const startYear = contractStartDate.getFullYear();
    const endYear = contractEndDate
      ? contractEndDate.getFullYear()
      : new Date().getFullYear();

    if (startYear > endYear) {
      return [];
    }

    const years: Array<{ label: string; value: number }> = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push({ label: year.toString(), value: year });
    }

    const currentYear = new Date().getFullYear();

    const filteredYears = years.filter((yearObj) => {
      // Only return current and previous years up from 2025
      return yearObj.value <= currentYear && yearObj.value >= 2025;
    });

    return filteredYears;
  }, [contractStartDate, contractEndDate]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const defaultYear = yearOptions.some(
      (option) => option.value === currentYear,
    )
      ? currentYear
      : (yearOptions[yearOptions.length - 1]?.value ?? null);

    setInvoicingYear(defaultYear);
  }, [selectedCompanyId, yearOptions]);

  const items = useCycleItems();
  const invoicingTypeItems = useInvoicingTypeItems();

  const search = (event: AutoCompleteCompleteEvent) => {
    // Timeout to emulate a network connection
    setTimeout(() => {
      let _filteredCompanies;

      if (!event.query.trim().length) {
        _filteredCompanies = [...(companies || [])];
      } else {
        _filteredCompanies = companies?.filter((company) => {
          const companyBusinessId = company.identifiers.find(
            (id) => id.identifierType.id === businessIdentifierId,
          )?.text;
          return (
            company.companyName
              .toLowerCase()
              .includes(event.query.toLowerCase()) ||
            (companyBusinessId &&
              companyBusinessId
                .toLowerCase()
                .includes(event.query.toLowerCase()))
          );
        });
      }

      _filteredCompanies = _filteredCompanies?.sort((a, b) =>
        a.companyName.localeCompare(b.companyName, undefined, {
          sensitivity: "base",
        }),
      );

      setFilteredCompanies(_filteredCompanies);
    }, 250);
  };

  const handleCompanySelect = (e: { value: Company }) => {
    const company = e.value;
    setSelectedCompany(company);
  };

  const handleCompanyChange = (e: { value: Company }) => {
    const company = e.value;
    setSearchedCompany(company);
  };

  const handleReportingTypeChange = (value: ReportingCycleEnum | undefined) => {
    setReportingType(value);
  };

  const hasChanged = () => {
    const savedCycle = reportingCycleData?.find(
      (cycle) => cycle.year === selectedYear,
    );

    return savedCycle?.cycle !== reportingType;
  };

  const handleSaveChanges = () => {
    const savedCycle = reportingCycleData?.find(
      (cycle) => cycle.year === selectedYear,
    );

    if (selectedCompanyId && selectedYear && reportingType !== undefined) {
      if (savedCycle) {
        // Update existing reporting cycle
        updateReportingCycle({
          id: savedCycle.id,
          companyId: selectedCompanyId,
          year: selectedYear,
          cycle: reportingType,
        });
      }
    }
  };

  const isCreatingReportingCycle = false; // Placeholder, replace with actual state

  const hasInvoicingTypeChanged = () => {
    const savedInvoicingType = invoicingTypes?.find(
      (type) => type.year === invoicingYear,
    );

    return savedInvoicingType?.invoicingType !== invoicingType;
  };

  const handleSaveInvoicingTypeChanges = () => {
    const savedInvoicingType = invoicingTypes?.find(
      (type) => type.year === invoicingYear,
    );

    if (!selectedCompanyId || !invoicingYear || invoicingType === undefined) {
      return;
    }

    if (savedInvoicingType) {
      updateInvoicingType({
        id: savedInvoicingType.id,
        companyId: selectedCompanyId,
        year: invoicingYear,
        invoicingType,
      });
    } else {
      createInvoicingType({
        companyId: selectedCompanyId,
        year: invoicingYear,
        invoicingType,
      });
    }
  };

  const actionsTemplate = (rowData: Organization) => {
    const company = selectedCompany;
    return (
      <ActionContainer>
        <Tooltip target={`.user-icon-${rowData.id}`} />
        <UserIcon
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate("/users?companyId=" + company?.id);
          }}
          className={`pi pi-users user-icon-${rowData.id}`}
          data-pr-tooltip={t("text-manage-company-users")}
          data-pr-position="top"
          tabIndex={0}
          role="button"
        />
        <Tooltip target={`.report-icon-${rowData.id}`} />
        <UserIcon
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate(
              `/reporting?companyId=${company?.id}&organizationId=${rowData.id}`,
            );
          }}
          className={`pi pi-file report-icon-${rowData.id}`}
          data-pr-tooltip={t("text-view-organization-reports")}
          data-pr-position="top"
          tabIndex={0}
          role="button"
        />
      </ActionContainer>
    );
  };

  const pipedriveIdentifier = company?.identifiers.find(
    (id) => id.identifierType.id === pipedriveIdentifierId,
  )?.text;
  const procountorIdentifier = company?.identifiers.find(
    (id) => id.identifierType.id === procountorIdentifierId,
  )?.text;

  return (
    <OrganizationContainer className="organization-view">
      <ContainerHeader
        title={t("header-organizations")}
        description="Morem ipsum dolor sit amet, consectetur adipiscing elit. Nunc vulputate libero et velit interdum, ac aliquet odio mattis."
      />
      {(showEditOrganization || showNewOrganization) && selectedCompanyId && (
        <EditOrganizationDialog
          organization={showEditOrganization || undefined}
          show={!!showEditOrganization || showNewOrganization}
          onHide={() => {
            setShowEditOrganization(null);
            setShowNewOrganization(false);
          }}
          companyId={selectedCompanyId}
          countries={countries}
        />
      )}
      <PaddedContainer className="padded-container">
        <LoadingContainer
          status={
            isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS
          }
        >
          {!companies && <div>{t("label-no-companies-found")}</div>}
          {companies && (
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <CompanySelect>
                <label>{t("label-active-company")}</label>
                <StyledAutoComplete
                  id="company"
                  value={searchedCompany}
                  suggestions={filteredCompanies}
                  dropdown
                  completeMethod={search}
                  field="companyName"
                  onChange={handleCompanyChange}
                  onSelect={handleCompanySelect}
                  placeholder={t("placeholder-select-company")!}
                  itemTemplate={(company: Company) => {
                    const businessId = company.identifiers.find(
                      (id) => id.identifierType.id === businessIdentifierId,
                    )?.text;
                    return businessId
                      ? `${company.companyName} - ${businessId}`
                      : company.companyName;
                  }}
                  selectedItemTemplate={(company: Company) => {
                    const businessId = company.identifiers.find(
                      (id) => id.identifierType.id === businessIdentifierId,
                    )?.text;
                    return businessId
                      ? `${company.companyName} - ${businessId}`
                      : company.companyName;
                  }}
                />
              </CompanySelect>
              {selectedCompanyId && (
                <Button
                  label={t("cmd-create-organization")}
                  icon="pi pi-plus"
                  onClick={() => setShowNewOrganization(true)}
                  style={{ marginBottom: "20px" }}
                />
              )}
            </div>
          )}
          {selectedCompanyId && (
            <ReportingTypeSelect>
              <label style={{ marginBottom: "5px" }}>
                {t("label-reporting-cycle")}
              </label>
              <Row>
                <YearSelectContainer>
                  <YearSelectDropdown
                    options={yearOptions}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.value)}
                    placeholder={t("placeholder-select-year")}
                  />
                </YearSelectContainer>
                <SelectButton
                  style={{ marginBottom: "5px" }}
                  optionLabel="name"
                  value={reportingType}
                  options={items}
                  onChange={(e) => handleReportingTypeChange(e.value)}
                  disabled={
                    isCreatingReportingCycle ||
                    isLoadingReportingCycle ||
                    !selectedYear
                  }
                />
                <Button
                  id="save-reporting-cycle-changes-btn"
                  label={t("cmd-update-changes")}
                  disabled={!hasChanged() || isCreatingReportingCycle}
                  onClick={handleSaveChanges}
                />
              </Row>
            </ReportingTypeSelect>
          )}

          {selectedCompanyId && (
            <LinksContainer>
              <StyledButton
                icon="pi pi-file"
                text
                onClick={() =>
                  navigate(
                    `/reporting?${selectedCompanyId ? `companyId=${selectedCompanyId}` : ""}`,
                  )
                }
              >
                {t("cmd-view-reports")}
              </StyledButton>
              {pipedriveIdentifier && (
                <StyledButton
                  icon="pi pi-refresh"
                  text
                  onClick={() => {
                    try {
                      updatePipeDriveInfo({
                        companyId: selectedCompanyId,
                      });
                    } catch (error) {
                      alert(
                        "Error parsing Pipedrive organization ID. Please check the identifier value and try again.",
                      );
                    }
                  }}
                  loading={isUpdatingPipeDriveInfo}
                >
                  {t("cmd-pipedrive-refresh")}
                </StyledButton>
              )}
            </LinksContainer>
          )}

          <CompanyContractInformation
            company={selectedCompany || undefined}
            pipedriveIdentifier={pipedriveIdentifier}
            procountorIdentifier={procountorIdentifier}
          />

          <CompanyInvoicingInformation company={selectedCompany || undefined} />

          {selectedCompanyId && (
            <ReportingTypeSelect>
              <label style={{ marginBottom: "5px" }}>
                {t("label-invoicing-type")}
              </label>
              <Row>
                <YearSelectContainer>
                  <YearSelectDropdown
                    options={yearOptions}
                    value={invoicingYear}
                    onChange={(e) => setInvoicingYear(e.value)}
                    placeholder={t("placeholder-select-year")}
                  />
                </YearSelectContainer>
                <InvoicingTypeDropdown
                  options={invoicingTypeItems}
                  optionLabel="name"
                  optionValue="value"
                  value={invoicingType}
                  onChange={(e) => setInvoicingType(e.value)}
                  placeholder={t("placeholder-select-invoicing-type")}
                  disabled={isLoadingInvoicingTypes || !invoicingYear}
                />
                <Button
                  id="save-invoicing-type-changes-btn"
                  label={t("cmd-update-changes")}
                  disabled={!hasInvoicingTypeChanged() || isSavingInvoicingType}
                  onClick={handleSaveInvoicingTypeChanges}
                />
              </Row>
            </ReportingTypeSelect>
          )}

          <SynkkaIdentifiers company={selectedCompany} isAdmin={true} />

          {selectedCompanyId && (
            <StyledTable
              value={filteredOrganizations}
              emptyMessage={t("label-no-organizations")}
              dataKey="id"
              scrollable
            >
              <Column
                field="organizationName"
                header={t("header-organization-name")}
              />
              <Column
                field="raports"
                header={t("header-reports")}
                body={(rowData) => <span>-</span>}
              />
              <Column
                field="created"
                header={t("header-created")}
                body={() => <span>-</span>}
              />
              <Column
                field="edit"
                body={(rowData: Organization) => (
                  <span
                    role="button"
                    tabIndex={0}
                    className="edit-organization-link"
                    onClick={() => setShowEditOrganization(rowData)}
                  >
                    {t("cmd-edit-product")}
                  </span>
                )}
                style={{ textAlign: "right" }}
              />

              <Column
                field="actions"
                body={actionsTemplate}
                style={{ width: "100px" }}
              />
            </StyledTable>
          )}
        </LoadingContainer>
      </PaddedContainer>
    </OrganizationContainer>
  );
};

export default AdminOrganizations;
